import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, LogIn, Settings, CreditCard, Wallet, Users } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const { theme } = useTheme();
  const { isLoggedIn, user, login, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownTimeout = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const navItems = [
    {
      label: 'Software',
      children: [
        { label: 'Forge Studio', description: 'Desktop IDE for ROS2', path: '/download' },
        { label: 'Forge Cloud', description: 'Fleet management & telemetry', tag: 'Coming Soon' },
      ],
    },
    {
      label: 'Hardware',
      children: [
        { label: 'Sensors & LiDAR', description: 'Outdoor-rated, India-ready', tag: 'Coming Soon' },
        { label: 'Dev Boards', description: 'Compute modules & kits', tag: 'Coming Soon' },
        { label: 'Forge Certified', description: 'Third-party verified hardware', tag: 'Coming Soon' },
      ],
    },
    ...(isLoggedIn ? [{
      label: 'Marketplace',
      path: '/marketplace',
    }] : []),
    {
      label: 'Solutions',
      children: [
        { label: 'Hobbyists & Students', description: 'Learn robotics with free tools', action: () => scrollToSection('solutions') },
        { label: 'Startups', description: 'Ship robots faster', action: () => scrollToSection('solutions') },
        { label: 'Enterprise', description: 'Fleet-scale deployment', path: '/pricing' },
      ],
    },
    { label: 'Pricing', path: '/pricing' },
  ];

  const openDrop = (label) => {
    clearTimeout(dropdownTimeout.current);
    setOpenDropdown(label);
  };
  const closeDrop = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-smooth ${
        isScrolled ? 'glass-frosted glow-subtle' : ''
      }`}
      style={{ padding: '1rem 2rem' }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center">
            <img src={theme === 'dark' ? '/logo-dark.png' : '/logo-light.png'} alt="Forge Studio Logo" className="w-full h-full object-contain rounded-lg transition-opacity duration-300" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-bold text-xl tracking-tight text-foreground-primary">Forge</span>
            <span className="text-sm text-foreground-muted font-medium">by Vinayak Robotics</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => openDrop(item.label)}
                onMouseLeave={closeDrop}
              >
                <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg hover:bg-subtle">
                  {item.label}
                  <ChevronDown size={12} className={`transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === item.label && (
                  <div className="absolute top-full left-0 mt-1 glass-frosted rounded-xl p-2 min-w-[260px] glow-subtle">
                    {item.children.map((child) => (
                      <div
                        key={child.label}
                        className="rounded-lg hover:bg-subtle transition-smooth"
                      >
                        {child.path ? (
                          <Link to={child.path} className="block px-4 py-3" onClick={() => setOpenDropdown(null)}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground-primary">{child.label}</span>
                              {child.tag && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-subtle text-foreground-muted">{child.tag}</span>
                              )}
                            </div>
                            <p className="text-xs text-foreground-muted mt-0.5">{child.description}</p>
                          </Link>
                        ) : child.action ? (
                          <button onClick={() => { child.action(); setOpenDropdown(null); }} className="block w-full text-left px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground-primary">{child.label}</span>
                              {child.tag && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-subtle text-foreground-muted">{child.tag}</span>
                              )}
                            </div>
                            <p className="text-xs text-foreground-muted mt-0.5">{child.description}</p>
                          </button>
                        ) : (
                          <div className="px-4 py-3 opacity-60 cursor-default">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground-primary">{child.label}</span>
                              {child.tag && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-subtle text-foreground-muted">{child.tag}</span>
                              )}
                            </div>
                            <p className="text-xs text-foreground-muted mt-0.5">{child.description}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : item.action ? (
              <button
                key={item.label}
                onClick={item.action}
                className="px-3 py-2 text-sm font-medium text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg hover:bg-subtle"
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.path}
                className="px-3 py-2 text-sm font-medium text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg hover:bg-subtle"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        {/* Right */}
        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle />
          <a
            href="https://forums.vinayakrobotics.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-smooth"
            style={{ border: '1px solid rgba(249,115,22,0.4)', color: '#F97316' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(249,115,22,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Forums
          </a>
          {isLoggedIn ? (
            <div
              className="relative"
              onMouseEnter={() => openDrop('profile')}
              onMouseLeave={closeDrop}
            >
              <button
                onClick={() => navigate('/account')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-smooth hover:ring-2 hover:ring-orange-500/50"
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: '#F97316', color: '#0B0E13' }}>
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <ChevronDown size={12} className={`text-foreground-secondary transition-transform ${openDropdown === 'profile' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'profile' && (
              <div className="absolute right-0 top-full mt-1 glass-frosted rounded-xl p-2 min-w-[220px] glow-subtle">
                <div className="px-4 py-3 border-b border-subtle">
                  <p className="text-sm font-medium text-foreground-primary truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
                </div>
                <div className="py-1">
                  <Link to="/account" onClick={() => setOpenDropdown(null)} className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg hover:bg-subtle">
                    <Settings size={14} />
                    Account Settings
                  </Link>
                  <Link to="/account?tab=billing" onClick={() => setOpenDropdown(null)} className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg hover:bg-subtle">
                    <CreditCard size={14} />
                    Billing
                  </Link>
                  <Link to="/account?tab=payment" onClick={() => setOpenDropdown(null)} className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg hover:bg-subtle">
                    <Wallet size={14} />
                    Payment Settings
                  </Link>
                  <Link to="/account?tab=team" onClick={() => setOpenDropdown(null)} className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg hover:bg-subtle">
                    <Users size={14} />
                    Team Management
                  </Link>
                </div>
                <div className="border-t border-subtle pt-1">
                  <button onClick={() => { setOpenDropdown(null); logout(); }} className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 transition-smooth rounded-lg hover:bg-subtle">
                    <LogIn size={14} className="rotate-180" />
                    Sign Out
                  </button>
                </div>
              </div>
              )}
            </div>
          ) : (
            <button
              onClick={login}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-smooth"
              style={{ background: '#F97316', color: '#0B0E13' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(249,115,22,0.5)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              <LogIn size={16} />
              Sign In
            </button>
          )}
        </div>

        {/* Mobile Toggle */}
        <button className="lg:hidden text-foreground-primary p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden mt-3 glass-frosted rounded-xl p-5 space-y-3 max-h-[80vh] overflow-y-auto">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label}>
                <button
                  onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                  className="flex items-center justify-between w-full text-left py-2 text-sm font-medium text-foreground-muted"
                >
                  {item.label}
                  <ChevronDown size={14} className={`transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === item.label && (
                  <div className="pl-4 space-y-1 mt-1">
                    {item.children.map((child) => (
                      <div key={child.label}>
                        {child.path ? (
                          <Link to={child.path} onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-sm text-foreground-muted hover:text-forge-400">
                            {child.label} {child.tag && <span className="text-[10px] text-foreground-faint">({child.tag})</span>}
                          </Link>
                        ) : child.action ? (
                          <button onClick={() => { child.action(); setIsMobileMenuOpen(false); }} className="block py-2 text-sm text-foreground-muted hover:text-forge-400">
                            {child.label}
                          </button>
                        ) : (
                          <span className="block py-2 text-sm text-foreground-faint">{child.label} ({child.tag})</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : item.action ? (
              <button key={item.label} onClick={item.action} className="block w-full text-left py-2 text-sm font-medium text-foreground-muted">
                {item.label}
              </button>
            ) : (
              <Link key={item.label} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-sm font-medium text-foreground-muted">
                {item.label}
              </Link>
            )
          )}
          <a
            href="https://forums.vinayakrobotics.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-2.5 rounded-lg text-sm font-semibold mt-2"
            style={{ border: '1px solid rgba(249,115,22,0.4)', color: '#F97316' }}
          >
            Forums
          </a>
          {isLoggedIn ? (
            <div className="border-t border-subtle mt-3 pt-3 space-y-1">
              <div className="flex items-center gap-3 px-2 py-2">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: '#F97316', color: '#0B0E13' }}>
                    {(user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-primary truncate">{user?.name || 'Account'}</p>
                  <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
                </div>
              </div>
              <Link to="/account" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg">
                <Settings size={14} /> Account Settings
              </Link>
              <Link to="/account?tab=billing" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg">
                <CreditCard size={14} /> Billing
              </Link>
              <Link to="/account?tab=payment" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg">
                <Wallet size={14} /> Payment Settings
              </Link>
              <Link to="/account?tab=team" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 text-sm text-foreground-secondary hover:text-foreground-primary transition-smooth rounded-lg">
                <Users size={14} /> Team Management
              </Link>
              <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-2 py-2 text-sm text-red-400 hover:text-red-300 transition-smooth rounded-lg">
                <LogIn size={14} className="rotate-180" /> Sign Out
              </button>
            </div>
          ) : (
            <button
              onClick={() => { login(); setIsMobileMenuOpen(false); }}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-semibold mt-2"
              style={{ background: '#F97316', color: '#0B0E13' }}
            >
              <LogIn size={16} />
              Sign In
            </button>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
